import { motion, AnimatePresence } from 'motion/react';
import { FileUp, Scan, Sparkles, CheckCircle2, Receipt, FileText, DollarSign, Calendar, Table } from 'lucide-react';
import { useEffect, useState } from 'react';
import { primaryColor } from 'Common/commaon';
import { RichEditor } from './richEditor';
import { useRef } from 'react';


interface DocumentLoaderProps {
  stage:
    | 'uploading'
    | 'routing'
    | 'extracting'
    | 'streaming'
    | 'processing'
    | 'complete';
  extractedHTML?: any;
}

const loadingTips = [
  "Logan is analyzing line items to build your invoice table",
  "Pro tip: Logan works best with clear, high-quality PDF scans",
  "Logan can extract materials, labor costs, and scope details automatically",
  "Save hours on contract prep - Logan processes PDFs in seconds",
  "Logan helps contractors stay organized and update scope of work faster",
  "Your invoice data will be ready to add to contracts instantly"
];

const stageConfig = {
  uploading: {
    icon: FileUp,
    title: 'Uploading Your PDF',
    description: 'Logan is receiving your invoice document...',
    color: 'from-blue-500 to-cyan-500'
  },
  extracting: {
    icon: Scan,
    title: 'Logan is Scanning',
    description: 'Reading invoice details, line items, and costs...',
    color: 'from-purple-500 to-pink-500'
  },
  routing: {
    icon: Scan,
    title: "Routing Document",
    description: "Logan is deciding best extraction strategy...",
    color: "from-indigo-500 to-purple-500"
  },
  streaming: {
    icon: Table,
    title: "Extracting Tables",
    description: "Logan is streaming structured content...",
    color: "from-orange-500 to-yellow-500"
  },
  processing: {
    icon: Sparkles,
    title: 'Building Invoice Table',
    description: 'Logan is organizing data for your contract and scope of work...',
    color: 'from-orange-500 to-yellow-500'
  },
  complete: {
    icon: CheckCircle2,
    title: 'Ready for Contract!',
    description: 'Logan has extracted your invoice table successfully',
    color: 'green-500 to-emerald-500'
  }
};

const floatingIcons = [Receipt, FileText, DollarSign, Calendar];

export function DocumentLoader({ stage ,extractedHTML}: DocumentLoaderProps) {
  const [tip, setTip] = useState(loadingTips[0]);
  const [progress, setProgress] = useState(0);
  const config = stageConfig[stage];
  const Icon = config.icon;
  const contentRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if(!extractedHTML){
      const interval = setInterval(() => {
        setTip(loadingTips[Math.floor(Math.random() * loadingTips.length)]);
      }, 3000);
      return () => clearInterval(interval);
    }
 
  }, [extractedHTML]);

  useEffect(() => {
    if(!extractedHTML){
      setProgress(0); // ✅ reset on stage change
  
      const targetProgress =
        stage === 'uploading' ? 25 :
        stage === 'routing' ? 40 :
        stage === 'extracting' ? 60 :
        stage === 'streaming' ? 80 :
        stage === 'processing' ? 95 :
        100;
    
      let current = 0;
    
      const interval = setInterval(() => {
        current += 1;
        setProgress(prev => {
          if (prev >= targetProgress) {
            clearInterval(interval);
            return targetProgress;
          }
          return prev + 1;
        });
      }, 40);
    return () => clearInterval(interval);

    }
   
  
  }, [stage,extractedHTML]);
  
  useEffect(() => {
    if (extractedHTML && contentRef.current) {
      contentRef.current.scrollTo({
        top: contentRef.current.scrollHeight,
        behavior: 'smooth'
      });
    }
  }, [extractedHTML]);
  
  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        backgroundColor: "rgba(0, 0, 0, 0.45)",
        backdropFilter: "blur(6px)",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        padding: "20px",
        zIndex: 9999
      }}
    >
      <div style={{ width: "100%", maxWidth: "650px" }}>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-3xl shadow-2xl p-8 md:p-12 relative overflow-hidden"
          style={{
            backgroundColor: "white",
            borderRadius: "24px",
            position: "relative",
            boxShadow: "0px 20px 40px rgba(0,0,0,0.15)"
          }}
        >
          {/* Floating background icons */}
          {!extractedHTML &&   <div
            className="absolute inset-0 overflow-hidden pointer-events-none opacity-5"
            style={{ position: "absolute", inset: 0, overflow: "hidden", opacity: 0.05, pointerEvents: "none" }}
          >
            {floatingIcons.map((FloatIcon, index) => (
              <motion.div
                key={index}
                className="absolute"
                style={{ position: "absolute" }}
                initial={{
                  x: `${Math.random() * 100}%`,
                  y: `${Math.random() * 100}%`,
                  rotate: 0
                }}
                animate={{
                  y: [`${Math.random() * 100}%`, `${Math.random() * 100}%`],
                  rotate: 360
                }}
                transition={{
                  duration: 10 + index * 2,
                  repeat: Infinity,
                  ease: "linear"
                }}
              >
                <FloatIcon className="w-16 h-16 md:w-24 md:h-24" />
              </motion.div>
            ))}
          </div>}
       

          {/* Main content */}
          <div className="relative z-10 space-y-8" style={{ position: "relative", zIndex: 10 }}>
           {!extractedHTML &&  <div className="flex justify-center" style={{ display: "flex", justifyContent: "center" }}>
              <motion.div
                key={stage}
                initial={{ scale: 0, rotate: -180 }}
                animate={{ scale: 1, rotate: 0 }}
                transition={{ type: "spring", stiffness: 200, damping: 15 }}
                className={`w-24 h-24 rounded-full  flex items-center justify-center shadow-xl`}
                style={{
                  width: "96px",
                  height: "96px",
                  backgroundColor:primaryColor,
                  borderRadius: "9999px",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  boxShadow: "0 10px 20px rgba(0,0,0,0.15)"
                }}
              >
                <motion.div
                  animate={stage !== 'complete' ? { rotate: 360 } : {}}
                  transition={{
                    duration: 2,
                    repeat: stage !== 'complete' ? Infinity : 0,
                    ease: "linear"
                  }}
                >
                  <Icon className="w-12 h-12 text-white" />
                </motion.div>
              </motion.div>
            </div>}
          

            {/* Stage Title & Description */}
            {!extractedHTML  &&  <div className="text-center space-y-2" style={{ textAlign: "center" }}>
              <AnimatePresence mode="wait">
                <motion.h2
                  key={stage + '-title'}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  className="text-slate-900"
                >
                  {config.title}
                </motion.h2>
              </AnimatePresence>

              <AnimatePresence mode="wait">
                <motion.p
                  key={stage + '-desc'}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="text-slate-600"
                >
                  {config.description}
                </motion.p>
              </AnimatePresence>
            </div>}
           

            {/* Progress Bar */}
            { !extractedHTML &&   <div className="space-y-2">
              <div className="h-3 bg-slate-100 rounded-full overflow-hidden"
                style={{
                  height: "12px",
                  backgroundColor: "#f1f5f9",
                  borderRadius: "9999px",
                  overflow: "hidden"
                }}
              >
                <motion.div
                  className={`h-full bg-gradient-to-r ${config.color} rounded-full relative`}
                  initial={{ width: 0 }}
                  animate={{ width: `${progress}%` }}
                  transition={{ duration: 0.3 }}
                  style={{
                    height: "100%",
                    borderRadius: "9999px",
                    position: "relative"
                  }}
                >
                  <motion.div
                    className="absolute inset-0 bg-gray-400"
                    animate={{ x: ["-100%", "100%"] }}
                    transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                    style={{
                      position: "absolute",
                      inset: 0,
                      backgroundColor:primaryColor

                    }}
                  />
                </motion.div>
              </div>

              <div className="flex justify-between text-slate-500"
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  color: "#64748b",
                }}
              >
                <span className=''>{Math.round(progress)}%</span>
                <span style={{ color: "#94a3b8" }}>
                  {stage === 'complete' ? 'Done!' : 'Processing...'}
                </span>
              </div>
            </div>}
          

            {/* Loading Tip */}
            {stage !== 'complete' && stage !== 'streaming' &&  !extractedHTML&& (
              <motion.div
                className="bg-gradient-to-br from-slate-50 to-blue-50 rounded-2xl p-6 border border-slate-100"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                style={{
                  background: "linear-gradient(to bottom right, #f8fafc, #eff6ff)",
                  borderRadius: "16px",
                  padding: "24px",
                  border: "1px solid #e2e8f0"
                }}
              >
                <div
                  className="flex items-start gap-3"
                  style={{ display: "flex", gap: "12px", alignItems: "flex-start" }}
                >
                  <motion.div
                    animate={{ rotate: [0, 10, -10, 0] }}
                    transition={{ duration: 2, repeat: Infinity }}
                  >
                    <Sparkles className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
                  </motion.div>

                  <div style={{ flex: 1 }}>
                    <div className="flex items-center gap-2 mb-1"
                      style={{ display: "flex", gap: "6px", marginBottom: "4px" }}
                    >
                      <span style={{ color: "#2563eb" }}>Logan AI</span>
                    </div>

                    <AnimatePresence mode="wait">
                      <motion.p
                        key={tip}
                        initial={{ opacity: 0, y: 5 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -5 }}
                        style={{ color: "#334155" }}
                      >
                        {tip}
                      </motion.p>
                    </AnimatePresence>
                  </div>
                </div>
              </motion.div>
            )}

            {/* Processing Dots */}
            {stage !== 'complete' && !extractedHTML && (
              <div className="flex justify-center gap-2"
                style={{ display: "flex", justifyContent: "center", gap: "8px" }}
              >
                {[0, 1, 2].map((index) => (
                  <motion.div
                    key={index}
                    className={`w-2 h-2 rounded-full bg-gradient-to-r ${config.color}`}
                    animate={{
                      scale: [1, 1.5, 1],
                      opacity: [0.3, 1, 0.3]
                    }}
                    transition={{
                      duration: 1.5,
                      repeat: Infinity,
                      delay: index * 0.2
                    }}
                    style={{
                      width: "8px",
                      height: "8px",
                      borderRadius: "9999px"
                    }}
                  />
                ))}
              </div>
            )}
            {extractedHTML && <div className='font-semibold text-lg'>
              Extracted Scope of Work
              </div>}
{extractedHTML && (
  <div
    ref={contentRef}
    className="prose max-w-none h-[500px] overflow-y-auto"
  >
    <RichEditor content={extractedHTML} />
  </div>
)}

            {/* Success Confetti */}
            {stage === 'complete' && (
              <div className="absolute inset-0 pointer-events-none"
                style={{ position: "absolute", inset: 0, pointerEvents: "none" }}
              >
                {[...Array(20)].map((_, i) => (
                  <motion.div
                    key={i}
                    className="absolute w-2 h-2 bg-gradient-to-r from-green-400 to-emerald-400 rounded-full"
                    initial={{
                      x: "50%",
                      y: "50%",
                      scale: 0,
                      opacity: 1
                    }}
                    animate={{
                      x: `${Math.random() * 100}%`,
                      y: `${Math.random() * 100}%`,
                      scale: [0, 1, 0],
                      opacity: [1, 1, 0]
                    }}
                    transition={{
                      duration: 1.5,
                      ease: "easeOut"
                    }}
                    style={{
                      position: "absolute",
                      width: "8px",
                      height: "8px",
                      borderRadius: "9999px",
                      background: "linear-gradient(to right, #4ade80, #34d399)"
                    }}
                  />
                ))}
              </div>
            )}

          </div>
        </motion.div>
      </div>
    </div>
  );
}
